/**
 * Async tenant-slug resolver — calls the backend to figure out whether
 * a typed-in tenant slug belongs to the same company as the user's
 * canonical slug. Used by `TenantMismatchHandler` to decide
 * "redirect to company URL" vs "logout".
 *
 * Backend endpoint: `GET /api/v1/public/resolve-slug?tenant=<slug>`
 *   Response: { canonical_slug, is_company, company_tenant_code }
 *   404 when the slug doesn't exist.
 *
 * Cached in-module (Map) for the lifetime of the SPA — slugs don't
 * change without a tenant rename, which is rare and invalidates the
 * cache via a full reload after the rename UI redirects.
 */

import api from '@services/api';

export interface SlugResolution {
  canonical_slug: string;
  is_company: boolean;
  company_tenant_code: string | null;
}

export type SlugLookupResult =
  | { status: 'found'; data: SlugResolution }
  | { status: 'not_found' }   // backend 404 — the slug doesn't exist
  | { status: 'error' };       // network failure / server crash etc.

const lookupCache = new Map<string, SlugLookupResult>();

/**
 * Full-fidelity lookup. Distinguishes "tenant doesn't exist" (404)
 * from "we couldn't reach the server" so callers can render the
 * right UX (a 404 page vs. a generic try-again-later message).
 *
 * Network errors are NOT cached — a transient outage shouldn't
 * poison subsequent attempts the moment connectivity returns.
 */
export async function lookupTenantSlug(slug: string): Promise<SlugLookupResult> {
  const key = slug.toLowerCase();
  const cached = lookupCache.get(key);
  if (cached) return cached;
  try {
    const res = await api.get('/public/resolve-slug', { params: { tenant: key } });
    const result: SlugLookupResult = {
      status: 'found',
      data: {
        canonical_slug: (res.data?.canonical_slug || '').toLowerCase(),
        is_company: !!res.data?.is_company,
        company_tenant_code: (res.data?.company_tenant_code || '').toLowerCase() || null,
      },
    };
    lookupCache.set(key, result);
    return result;
  } catch (err: any) {
    const status = err?.response?.status;
    if (status === 404) {
      const r: SlugLookupResult = { status: 'not_found' };
      lookupCache.set(key, r);
      return r;
    }
    // Don't cache network errors — try again next time.
    return { status: 'error' };
  }
}

/**
 * Backward-compatible thin wrapper. Returns the SlugResolution when
 * found, otherwise null (both 404 and network errors collapse to
 * null — callers that need to distinguish should use lookupTenantSlug).
 */
export async function resolveTenantSlug(slug: string): Promise<SlugResolution | null> {
  const r = await lookupTenantSlug(slug);
  return r.status === 'found' ? r.data : null;
}

/**
 * Resolve the `auth.transactional_email` flag for a tenant slug via the
 * public brand endpoint. Used by the branded login page to decide whether
 * to show the "forgot password?" affordance — hidden only when we can
 * POSITIVELY resolve the flag to false.
 *
 * Returns `boolean | null`: `null` means "unknown" (network error, missing
 * field, or slug not yet loaded) and callers MUST treat unknown as enabled
 * so the link is never hidden spuriously during load.
 */
// Module-scoped cache so we don't re-fetch /public/brand-from-tenant just for
// this flag: BrandResolver already fetches that endpoint (which now returns
// transactional_email) for every branded login surface and feeds the value in
// here via cacheTransactionalEmailForSlug. resolveTransactionalEmailForSlug
// reads the cache first and only fetches on a genuine miss.
const _transactionalEmailBySlug = new Map<string, boolean | null>();

export function cacheTransactionalEmailForSlug(slug: string, value: unknown): void {
  _transactionalEmailBySlug.set(slug.toLowerCase(), typeof value === 'boolean' ? value : null);
}

export async function resolveTransactionalEmailForSlug(slug: string): Promise<boolean | null> {
  const key = slug.toLowerCase();
  if (_transactionalEmailBySlug.has(key)) {
    return _transactionalEmailBySlug.get(key) ?? null;
  }
  try {
    const res = await api.get('/public/brand-from-tenant', { params: { tenant: key } });
    const value = res.data?.transactional_email;
    const resolved = typeof value === 'boolean' ? value : null;
    _transactionalEmailBySlug.set(key, resolved);
    return resolved;
  } catch {
    return null;
  }
}

/**
 * Vanity-domain → tenant-slug resolver. Maps the CURRENT host to the
 * tenant slug whose branded login a bare-domain visitor should land on
 * (e.g. `jewishpro.ai` → `m2-team`). Backed by
 * `GET /api/v1/public/brand-from-host`, which matches
 * `tenant.tenant_domain` (custom domain) or the leftmost subdomain
 * label. Returns the matched tenant's OWN `tenant_code` (preferred) or
 * its company ancestor's code; `null` when the host maps to no tenant
 * (localhost, the platform-owner host, or an unconfigured domain) — the
 * caller then falls back to the platform-default login.
 *
 * Caching is owned entirely by the caller (`vanityHost.resolveHostSlug`,
 * which persists positive results to localStorage). This stays a pure
 * API call so there's only ONE cache for the host→tenant fact.
 */
export async function resolveLoginSlugForHost(host: string): Promise<string | null> {
  try {
    const res = await api.get('/public/brand-from-host', { params: { host: host.toLowerCase() } });
    return (res.data?.tenant_code || res.data?.company_tenant_code || '').toLowerCase() || null;
  } catch {
    // Transient failure — return null; the caller retries on next load.
    return null;
  }
}
